import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import Avatar from "@/components/Avatar";
import CoachPhotoInput from "@/components/CoachPhotoInput";
import { GraduationCap, UserPlus, Check, X, Trash2, Pencil, Phone, Mail, MapPin, Building2 } from "lucide-react";

const EMPTY = { name: "", email: "", phone: "", facility: "", location: "", specialty: "", experience: "", bio: "", photo: "" };

export default function CoachManager({ onChange }) {
  const [coaches, setCoaches] = useState([]);
  const [apps, setApps] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/coaches");
    setCoaches(data.coaches);
    setApps(data.applications);
  };
  useEffect(() => { load(); }, []);

  const notify = () => { load(); onChange && onChange(); };

  const addCoach = async () => {
    if (!form.name.trim()) return toast.error("Coach name is required");
    setBusy(true);
    try {
      await api.post("/admin/coaches", { ...form, active: true });
      toast.success(`${form.name} added to your coaching staff`);
      setForm(EMPTY);
      notify();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add coach"); }
    finally { setBusy(false); }
  };

  const toggleActive = async (c) => {
    await api.put(`/admin/coaches/${c.id}`, { ...c, active: !c.active });
    toast.success(`${c.name} ${c.active ? "deactivated" : "activated"}`);
    notify();
  };

  const saveEdit = async () => {
    if (!editing.name.trim()) return toast.error("Coach name is required");
    setBusy(true);
    try {
      await api.put(`/admin/coaches/${editing.id}`, editing);
      toast.success("Coach updated");
      setEditing(null);
      notify();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const removeCoach = async (c) => {
    if (!window.confirm(`Remove ${c.name} from your coaching staff?`)) return;
    try {
      await api.delete(`/admin/coaches/${c.id}`);
      toast.success(`${c.name} removed`);
      notify();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const approve = async (a) => {
    try {
      await api.post(`/admin/coaches/applications/${a.id}/approve`);
      toast.success(`${a.name} approved — now an active coach`);
      notify();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };

  const reject = async (a) => {
    if (!window.confirm(`Reject and delete ${a.name}'s application?`)) return;
    await api.delete(`/admin/coaches/applications/${a.id}`);
    toast.success("Application removed");
    notify();
  };

  const pending = apps.filter((a) => a.status === "pending");
  const input = "w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red text-sm";

  return (
    <div className="mt-8" data-testid="coach-manager">
      <h3 className="font-display font-bold text-2xl uppercase mb-1 flex items-center gap-2">
        <GraduationCap className="w-6 h-6 text-pb-red" /> Coaches System
        {pending.length > 0 && (
          <span data-testid="pending-apps-badge" className="bg-pb-red text-black text-xs font-bold px-2 py-0.5">{pending.length} pending</span>
        )}
      </h3>
      <p className="text-sm text-zinc-500 mb-5">Approve coach applications, manage your active staff, and assign coaches to sessions. Coaches assigned to upcoming sessions can't be deleted.</p>

      {pending.length > 0 && (
        <div className="bg-pb-surface border border-pb-cyan/40 p-5 mb-5" data-testid="coach-applications">
          <div className="font-display font-bold text-lg uppercase mb-3">Pending Applications</div>
          <div className="space-y-3">
            {pending.map((a) => (
              <div key={a.id} data-testid={`coach-app-${a.id}`} className="border border-pb-border bg-pb-elevated p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="text-sm space-y-1">
                    <div className="font-display font-bold uppercase text-base">{a.name}</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-400">
                      <span className="flex items-center gap-1"><Phone className="w-3 h-3 text-pb-cyan" />{a.phone}</span>
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3 text-pb-cyan" />{a.email}</span>
                      {a.facility && <span className="flex items-center gap-1"><Building2 className="w-3 h-3 text-pb-cyan" />{a.facility}</span>}
                      {a.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-pb-cyan" />{a.location}</span>}
                    </div>
                    {(a.specialty || a.experience) && (
                      <div className="text-xs text-zinc-500">{[a.specialty, a.experience].filter(Boolean).join(" · ")}</div>
                    )}
                    {a.message && <div className="text-xs text-zinc-400 italic">"{a.message}"</div>}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button data-testid={`approve-app-${a.id}`} onClick={() => approve(a)}
                      className="flex items-center gap-1.5 bg-pb-red text-black font-bold uppercase tracking-widest text-xs px-4 py-2 hover:bg-[#BEF264] active:scale-95 transition-all">
                      <Check className="w-3.5 h-3.5" /> Approve
                    </button>
                    <button data-testid={`reject-app-${a.id}`} onClick={() => reject(a)}
                      className="flex items-center gap-1.5 border border-pb-border text-zinc-400 font-bold uppercase tracking-widest text-xs px-4 py-2 hover:border-red-500 hover:text-red-400 transition-colors">
                      <X className="w-3.5 h-3.5" /> Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-pb-surface border border-pb-border p-5">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus className="w-4 h-4 text-pb-red" />
            <span className="font-display font-bold text-lg uppercase">Add Coach Manually</span>
          </div>
          <div className="space-y-2.5">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Coach Photo</label>
              <CoachPhotoInput value={form.photo} name={form.name} onChange={(v) => setForm({ ...form, photo: v })} testId="new-coach-photo" />
            </div>
            {[["name", "Name *"], ["email", "Email"], ["phone", "Phone"], ["facility", "Facility"], ["location", "Location"], ["specialty", "Specialty"], ["experience", "Experience"]].map(([k, l]) => (
              <div key={k}>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{l}</label>
                <input data-testid={`new-coach-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={input} />
              </div>
            ))}
            <button data-testid="add-coach-btn" onClick={addCoach} disabled={busy}
              className="w-full bg-pb-red text-black font-bold uppercase tracking-widest py-2.5 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
              {busy ? "Adding…" : "Add Coach"}
            </button>
          </div>
        </div>

        <div className="lg:col-span-2 bg-pb-surface border border-pb-border">
          <div className="p-5 border-b border-pb-border font-display font-bold text-lg uppercase">
            Coaching Staff ({coaches.length})
          </div>
          <div className="divide-y divide-pb-border/50 max-h-[520px] overflow-y-auto">
            {coaches.length === 0 && <div className="p-5 text-sm text-zinc-600">No coaches yet — add one or approve an application.</div>}
            {coaches.map((c) => (
              <div key={c.id} data-testid={`coach-row-${c.id}`} className={`p-4 flex items-start justify-between gap-3 ${!c.active ? "opacity-50" : ""}`}>
                <Avatar src={c.photo} name={c.name} size={44} testId={`coach-row-avatar-${c.id}`} />
                <div className="flex-1 text-sm space-y-1 min-w-0">
                  <div className="font-display font-bold uppercase flex items-center gap-2">
                    {c.name}
                    {c.source === "application" && <span className="text-[9px] uppercase tracking-widest text-pb-cyan border border-pb-cyan/30 px-1.5">Applied</span>}
                    {!c.active && <span className="text-[9px] uppercase tracking-widest text-zinc-500 border border-pb-border px-1.5">Inactive</span>}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-400">
                    {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3 text-pb-cyan" />{c.phone}</span>}
                    {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3 text-pb-cyan" />{c.email}</span>}
                    {c.facility && <span className="flex items-center gap-1"><Building2 className="w-3 h-3 text-pb-cyan" />{c.facility}</span>}
                  </div>
                  {(c.specialty || c.experience) && <div className="text-xs text-zinc-500">{[c.specialty, c.experience].filter(Boolean).join(" · ")}</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Switch data-testid={`coach-active-${c.id}`} checked={c.active} onCheckedChange={() => toggleActive(c)}
                    className="data-[state=checked]:bg-pb-red" />
                  <button data-testid={`edit-coach-${c.id}`} onClick={() => setEditing({ ...c })}
                    className="w-8 h-8 flex items-center justify-center border border-pb-border hover:border-pb-cyan hover:text-pb-cyan transition-colors">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button data-testid={`delete-coach-${c.id}`} onClick={() => removeCoach(c)}
                    className="w-8 h-8 flex items-center justify-center border border-pb-border hover:border-red-500 hover:text-red-400 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-pb-surface border border-pb-red w-full max-w-md p-5 animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="edit-coach-modal">
            <div className="flex items-center justify-between mb-4">
              <span className="font-display font-bold text-lg uppercase">Edit Coach</span>
              <button data-testid="edit-coach-close" onClick={() => setEditing(null)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-2.5">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Coach Photo</label>
                <CoachPhotoInput value={editing.photo} name={editing.name} onChange={(v) => setEditing({ ...editing, photo: v })} testId="edit-coach-photo" />
              </div>
              {[["name", "Name *"], ["email", "Email"], ["phone", "Phone"], ["facility", "Facility"], ["location", "Location"], ["specialty", "Specialty"], ["experience", "Experience"]].map(([k, l]) => (
                <div key={k}>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{l}</label>
                  <input data-testid={`edit-coach-${k}`} value={editing[k] || ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} className={input} />
                </div>
              ))}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Lounge Bio (shown to members)</label>
                <textarea data-testid="edit-coach-bio" rows={3} value={editing.bio || ""}
                  onChange={(e) => setEditing({ ...editing, bio: e.target.value })} className={`${input} resize-none`} />
              </div>
              <button data-testid="save-coach-btn" onClick={saveEdit} disabled={busy}
                className="w-full bg-pb-red text-black font-bold uppercase tracking-widest py-2.5 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
                {busy ? "Saving…" : "Save Coach"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
